/**
 * Render a model-written prompt as DOM nodes: #-headings, **bold**, `inline code`,
 * ordered and unordered lists (one nesting level), fenced code and paragraphs.
 * Every node is built with createElement/textContent, so model output is never parsed as HTML.
 * Copy buttons keep using the original string; this only changes how it is shown.
 */

import '../styles/render-prompt.css';

const STOP_WORDS = new Set((
    'the and for with that this from your you our are was were will would can could should have has had not but ' +
    'all any about into out who what when where why how its their them they then than there here just also more ' +
    'most some such very each per via too use using get make need want like help please'
).split(' '));

const WORD = /[\p{L}\p{N}]+/gu;

/** Lowercased words of 3+ letters the visitor typed, minus stop words. */
export function typedWordSet(text) {
    const words = new Set();
    for (const match of (text || '').matchAll(WORD)) {
        const word = match[0].toLowerCase();
        if (word.length >= 3 && /\p{L}/u.test(word) && !STOP_WORDS.has(word)) words.add(word);
    }
    return words;
}

/** Append `text` to `parent`, wrapping whole words in `typed` in a mark. Returns the mark count. */
function appendWithMarks(parent, text, typed) {
    if (!typed || typed.size === 0) {
        parent.appendChild(document.createTextNode(text));
        return 0;
    }

    const ranges = [];
    for (const match of text.matchAll(WORD)) {
        if (!typed.has(match[0].toLowerCase())) continue;
        const start = match.index;
        const end = start + match[0].length;
        const prev = ranges[ranges.length - 1];
        // Join marks separated only by spaces so a typed phrase reads as one run
        if (prev && /^[ \t]+$/.test(text.slice(prev.end, start))) {
            prev.end = end;
        } else {
            ranges.push({ start, end });
        }
    }

    let cursor = 0;
    for (const { start, end } of ranges) {
        if (start > cursor) parent.appendChild(document.createTextNode(text.slice(cursor, start)));
        const mark = document.createElement('span');
        mark.className = 'mark';
        mark.textContent = text.slice(start, end);
        parent.appendChild(mark);
        cursor = end;
    }
    if (cursor < text.length) parent.appendChild(document.createTextNode(text.slice(cursor)));
    return ranges.length;
}

/** Inline markup: **bold** and `code`. Returns the mark count. */
function appendInline(parent, text, typed) {
    let marks = 0;
    text.split(/(`[^`\n]+`|\*\*[^*\n]+?\*\*)/).forEach((part) => {
        if (!part) return;
        if (part.length > 2 && part.startsWith('`') && part.endsWith('`')) {
            const code = document.createElement('code');
            code.textContent = part.slice(1, -1);
            parent.appendChild(code);
        } else if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) {
            const strong = document.createElement('strong');
            marks += appendWithMarks(strong, part.slice(2, -2), typed);
            parent.appendChild(strong);
        } else {
            marks += appendWithMarks(parent, part, typed);
        }
    });
    return marks;
}

const FENCE = /^\s*```/;
const HEADING = /^\s{0,3}#{1,6}\s+(.*?)(?:\s+#+)?\s*$/;
const ORDERED = /^(\s*)(\d+)[.)]\s+(.*)$/;
const UNORDERED = /^(\s*)[-*•]\s+(.*)$/;

function listItem(line) {
    let m = line.match(ORDERED);
    if (m) return { indent: m[1].length, ordered: true, start: Number(m[2]), text: m[3] };
    m = line.match(UNORDERED);
    if (m) return { indent: m[1].length, ordered: false, start: 1, text: m[2] };
    return null;
}

function makeList(item) {
    const list = document.createElement(item.ordered ? 'ol' : 'ul');
    list.className = item.ordered ? 'pr-ol' : 'pr-ul';
    if (item.ordered && item.start !== 1) list.start = item.start;
    return list;
}

/** A trailing unclosed ** or ` would flash as raw characters while text types in; cut it. */
function trimOpenMarkup(text) {
    const lastBreak = text.lastIndexOf('\n') + 1;
    let tail = text.slice(lastBreak);
    if ((tail.match(/\*\*/g) || []).length % 2 === 1) tail = tail.slice(0, tail.lastIndexOf('**'));
    if (!FENCE.test(tail) && (tail.match(/`/g) || []).length % 2 === 1) tail = tail.slice(0, tail.lastIndexOf('`'));
    return text.slice(0, lastBreak) + tail.replace(/\*+$/, '');
}

/**
 * Replace the contents of `container` with the rendered prompt.
 * @param {HTMLElement} container
 * @param {string} text - the prompt exactly as returned
 * @param {{ typedPrompt?: string, partial?: boolean }} [options]
 *   typedPrompt: words the visitor typed get a `mark` highlight.
 *   partial: text is a prefix being typed in, so hide unfinished markup.
 * @returns {{ marks: number }}
 */
export function renderPrompt(container, text, { typedPrompt = '', partial = false } = {}) {
    const typed = typedPrompt ? typedWordSet(typedPrompt) : null;
    const lines = (partial ? trimOpenMarkup(text) : text).replace(/\r\n?/g, '\n').split('\n');
    const body = document.createElement('div');
    body.className = 'pr';
    let marks = 0;

    let paragraph = null;
    let list = null;
    let topList = null;
    let nested = null;

    const endBlocks = () => { paragraph = null; list = null; topList = null; nested = null; };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (FENCE.test(line)) {
            endBlocks();
            const codeLines = [];
            i++;
            while (i < lines.length && !FENCE.test(lines[i])) codeLines.push(lines[i++]);
            const pre = document.createElement('pre');
            pre.className = 'pr-pre';
            const code = document.createElement('code');
            code.textContent = codeLines.join('\n');
            pre.appendChild(code);
            body.appendChild(pre);
            continue;
        }

        if (!line.trim()) { endBlocks(); continue; }

        const heading = line.match(HEADING);
        if (heading) {
            endBlocks();
            const h = document.createElement('p');
            h.className = 'pr-h';
            marks += appendInline(h, heading[1], typed);
            body.appendChild(h);
            continue;
        }

        const item = listItem(line);
        if (item) {
            paragraph = null;
            const li = document.createElement('li');
            marks += appendInline(li, item.text, typed);

            if (!topList) {
                topList = makeList(item);
                body.appendChild(topList);
                list = topList;
                nested = null;
            } else if (item.indent >= 2 && topList.lastElementChild) {
                if (!nested) {
                    nested = makeList(item);
                    topList.lastElementChild.appendChild(nested);
                }
                list = nested;
            } else {
                nested = null;
                list = topList;
            }
            list.appendChild(li);
            continue;
        }

        // Plain text. An indented line right after a list item continues that item.
        if (topList && /^\s+/.test(line) && (nested || topList).lastElementChild) {
            const target = (nested || topList).lastElementChild;
            target.appendChild(document.createElement('br'));
            marks += appendInline(target, line.trim(), typed);
            continue;
        }

        topList = null; list = null; nested = null;
        if (!paragraph) {
            paragraph = document.createElement('p');
            paragraph.className = 'pr-p';
            body.appendChild(paragraph);
        } else {
            paragraph.appendChild(document.createElement('br'));
        }
        marks += appendInline(paragraph, line, typed);
    }

    container.replaceChildren(body);
    return { marks };
}
